import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_DURATION_MS = 120_000;

function getSupportedMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const mt of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (MediaRecorder.isTypeSupported(mt)) return mt;
  }
  return '';
}

async function acquireMicStream(): Promise<MediaStream> {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Your browser does not support audio recording. Try Chrome or Safari.');
  }

  // Try getUserMedia directly — this triggers the browser permission prompt
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (err: unknown) {
    // Diagnose the failure
    const name = err instanceof DOMException ? err.name : '';

    if (name === 'NotAllowedError') {
      throw new Error(
        'Microphone permission denied. Click the lock icon in the address bar and allow microphone access.',
      );
    }

    if (name === 'NotFoundError') {
      // Browser can't find a mic — likely macOS system permission blocking the browser entirely
      throw new Error(
        'Microphone not accessible. Go to System Settings → Privacy & Security → Microphone and make sure your browser is enabled.',
      );
    }

    throw new Error(
      `Microphone error (${name}): ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export function useAudioRecorder() {
  const [isRecording, setIsRecording] = useState(false);
  const [durationMs, setDurationMs] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const startTimeRef = useRef(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const rafRef = useRef<number>(0);
  const resolveRef = useRef<((blob: Blob | null) => void) | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    audioCtxRef.current?.close().catch(() => {});
    mediaRecorderRef.current = null;
    streamRef.current = null;
    analyserRef.current = null;
    audioCtxRef.current = null;
    chunksRef.current = [];
    setIsRecording(false);
    setDurationMs(0);
    setAudioLevel(0);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const updateLevel = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const v = (data[i] - 128) / 128;
      sum += v * v;
    }
    setAudioLevel(Math.min(1, Math.sqrt(sum / data.length) * 3));
    rafRef.current = requestAnimationFrame(updateLevel);
  }, []);

  const startRecording = useCallback(async () => {
    const mimeType = getSupportedMimeType();
    if (!mimeType) throw new Error('Audio recording not supported in this browser');

    const stream = await acquireMicStream();
    streamRef.current = stream;

    // Analyser for volume metering
    const ctx = new AudioContext();
    audioCtxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;

    chunksRef.current = [];
    const recorder = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const blob = chunksRef.current.length > 0
        ? new Blob(chunksRef.current, { type: mimeType })
        : null;
      resolveRef.current?.(blob);
      resolveRef.current = null;
    };

    recorder.start(250);
    startTimeRef.current = Date.now();
    setIsRecording(true);
    setDurationMs(0);

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setDurationMs(elapsed);
      if (elapsed >= MAX_DURATION_MS) {
        recorder.stop();
        if (timerRef.current) clearInterval(timerRef.current);
      }
    }, 200);

    rafRef.current = requestAnimationFrame(updateLevel);
  }, [updateLevel]);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state !== 'recording') {
        cleanup();
        resolve(null);
        return;
      }
      resolveRef.current = (blob) => {
        cleanup();
        resolve(blob);
      };
      recorder.stop();
    });
  }, [cleanup]);

  const cancelRecording = useCallback(() => {
    resolveRef.current = null;
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.onstop = null;
      recorder.stop();
    }
    cleanup();
  }, [cleanup]);

  return { isRecording, durationMs, audioLevel, startRecording, stopRecording, cancelRecording };
}
